/**
 * Jobs API
 * Endpoints for job management and status tracking
 *
 * UNIFIED ARCHITECTURE:
 * - All file-based jobs use POST /api/jobs/create
 * - All config-based jobs use POST /api/jobs/create-with-config
 * - No direct external API calls from frontend
 */

import apiClient from "./client";
import { JOB_ENDPOINTS } from "../services/api";
import {
  Job,
  JobListResponse,
  JobStatsResponse,
  JobType,
  JobStatus,
  PaginationParams,
  MultiCameraJobConfig,
  AnalysisResult,
} from "../types";

/**
 * Create new file-based analysis job
 *
 * @param jobType - Type of analysis to perform
 * @param file - Video, image, or 3D model file to analyze
 * @param onProgress - Optional upload progress callback
 * @returns Created job
 */
export const createJob = async (
  jobType: JobType,
  file: File,
  onProgress?: (progress: number) => void,
  isLiveDemo?: boolean,
  sessionData?: any,
  chatSessionId?: string,  // NEW
  llmProvider?: string,    // NEW
  llmModel?: string        // NEW
): Promise<Job> => {
  const formData = new FormData();
  formData.append("job_type", jobType);
  formData.append("file", file);

  if (isLiveDemo) {
    formData.append("is_live_demo", "true");
  }

  if (sessionData) {
    formData.append("session_data", JSON.stringify(sessionData));
  }

  // NEW: Chat tracking
  if (chatSessionId) {
    formData.append("chat_session_id", chatSessionId);
  }
  if (llmProvider) {
    formData.append("llm_provider", llmProvider);
  }
  if (llmModel) {
    formData.append("llm_model", llmModel);
  }

  const response = await apiClient.post<Job>(JOB_ENDPOINTS.CREATE, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percentCompleted);
      }
    },
  });

  return response.data;
};

/**
 * Create new config-based job (e.g., multi-camera system)
 *
 * @param jobType - Type of analysis to perform
 * @param config - Configuration object (e.g., camera settings)
 * @returns Created job
 */
export const createConfigJob = async (
  jobType: JobType,
  config: MultiCameraJobConfig
): Promise<Job> => {
  const response = await apiClient.post<Job>(JOB_ENDPOINTS.CREATE_WITH_CONFIG, {
    job_type: jobType,
    config,
  });

  return response.data;
};

/**
 * Get paginated list of jobs
 *
 * @param params - Pagination and filter parameters
 * @returns Paginated job list
 */
export const getJobs = async (
  params?: PaginationParams & {
    status_filter?: JobStatus;
    type_filter?: JobType;
  }
): Promise<JobListResponse> => {
  const response = await apiClient.get<JobListResponse>(JOB_ENDPOINTS.LIST, {
    params,
  });

  return response.data;
};

/**
 * Get job by ID
 *
 * @param jobId - Job UUID
 * @returns Job details with file URLs
 */
export const getJobById = async (jobId: string): Promise<Job> => {
  const response = await apiClient.get<Job>(JOB_ENDPOINTS.BY_ID(jobId));

  return response.data;
};

/**
 * Get job by display number
 *
 * @param jobNumber - Job number (e.g., 92)
 * @returns Job details
 */
export const getJobByNumber = async (jobNumber: number): Promise<Job> => {
  const response = await apiClient.get<Job>(JOB_ENDPOINTS.BY_NUMBER(jobNumber));

  return response.data;
};

/**
 * Delete job
 *
 * @param jobId - Job UUID
 */
export const deleteJob = async (jobId: string): Promise<void> => {
  await apiClient.delete(JOB_ENDPOINTS.DELETE(jobId));
};

/**
 * Get job statistics
 *
 * @returns Job stats (total, by status, by type)
 */
export const getJobStats = async (): Promise<JobStatsResponse> => {
  const response = await apiClient.get<JobStatsResponse>(JOB_ENDPOINTS.STATS);

  return response.data;
};

/**
 * Poll job status until completion
 *
 * @param jobId - Job UUID
 * @param interval - Polling interval in milliseconds (default: 3000)
 * @param onUpdate - Callback called on each status update
 * @returns Promise that resolves when job completes or fails
 */
export const pollJobStatus = async (
  jobId: string,
  interval: number = 3000,
  onUpdate?: (job: Job) => void
): Promise<Job> => {
  return new Promise((resolve, reject) => {
    const poll = async () => {
      try {
        const job = await getJobById(jobId);

        // Call update callback
        if (onUpdate) {
          onUpdate(job);
        }

        // Check if job is complete
        if (job.status === JobStatus.COMPLETED) {
          resolve(job);
        } else if (job.status === JobStatus.FAILED) {
          reject(new Error(job.error_message || "Job failed"));
        } else {
          // Continue polling
          setTimeout(poll, interval);
        }
      } catch (error) {
        reject(error);
      }
    };

    poll();
  });
};

/**
 * Analyze a single frame for live demo (real-time analysis)
 *
 * @param frame - Image blob to analyze
 * @param jobType - Type of analysis to perform
 * @param cancelToken - Optional axios cancel token for aborting request
 * @returns Analysis results
 */
export const analyzeFrame = async (
  frame: Blob,
  jobType: JobType,
  cancelToken?: any
): Promise<AnalysisResult> => {
  const formData = new FormData();
  formData.append("job_type", jobType);
  formData.append("frame", frame, "frame.jpg");

  const response = await apiClient.post<AnalysisResult>(
    JOB_ENDPOINTS.ANALYZE_FRAME,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      cancelToken,
    }
  );

  return response.data;
};

/**
 * Create multiple polling instances for batch job tracking
 *
 * @param jobIds - Array of job UUIDs
 * @param interval - Polling interval in milliseconds
 * @param onUpdate - Callback for individual job updates
 * @returns Promise that resolves when all jobs complete
 */
export const pollMultipleJobs = async (
  jobIds: string[],
  interval: number = 3000,
  onUpdate?: (jobId: string, job: Job) => void
): Promise<Job[]> => {
  const promises = jobIds.map((jobId) =>
    pollJobStatus(jobId, interval, (job) => {
      if (onUpdate) {
        onUpdate(jobId, job);
      }
    })
  );

  return Promise.all(promises);
};
